import { useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  IDLE,
  MESSAGE_TEMPLATES,
  TYPING_TICK_MS,
  mayReplace,
  templateShown,
  templateTyping,
  writtenBy,
  type MessageTemplate,
  type TemplateTyping,
} from './messageTemplates';

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Types a picked template into the message through `setBody`, the same path the person's own typing takes,
 * so the saved text stays the one source. `stop` keeps what is shown; call it on any keystroke, paste or tap
 * in the message.
 */
export function useTemplateTyping(body: string, setBody: (body: string) => void) {
  const [state, dispatch] = useReducer(templateTyping, IDLE);
  // The caller's setter changes every render; a stale one in the tick would write to an old draft.
  const write = useRef(setBody);
  useLayoutEffect(() => {
    write.current = setBody;
  });

  // Keyed on the text, not on each tick: ticks that land together render once, and the last of them may
  // already have finished the template.
  const typed = writtenBy(state)?.text ?? null;
  useEffect(() => {
    if (typed !== null) write.current(typed);
  }, [typed]);

  const typing = state.kind === 'typing';
  useEffect(() => {
    if (!typing) return;
    const timer = setInterval(() => dispatch({ type: 'tick' }), TYPING_TICK_MS);
    return () => clearInterval(timer);
  }, [typing]);

  const start = (template: MessageTemplate) => {
    if (prefersReducedMotion()) {
      setBody(template.body);
      dispatch({ type: 'insert', template });
    } else {
      dispatch({ type: 'type', template });
    }
  };

  return {
    state,
    typing,
    pick: (template: MessageTemplate) =>
      mayReplace(state, body) ? start(template) : dispatch({ type: 'confirm', template }),
    replace: () => state.kind === 'confirming' && start(state.template),
    keep: () => dispatch({ type: 'keep' }),
    stop: () => dispatch({ type: 'stop' }),
    selected: templateShown(state, body),
  };
}

/** "Start from" chips, and the inline ask before a template replaces the person's own text. */
export function TemplateChips({
  state,
  selected,
  pick,
  replace,
  keep,
}: Pick<ReturnType<typeof useTemplateTyping>, 'pick' | 'replace' | 'keep' | 'selected'> & { state: TemplateTyping }) {
  return (
    <div className="mb-5 space-y-2">
      <p id="express-start-from" className="text-sm font-medium text-foreground">
        Start from
      </p>
      <div role="group" aria-labelledby="express-start-from" className="flex flex-wrap gap-2">
        {MESSAGE_TEMPLATES.map((template) => (
          <button
            key={template.key}
            type="button"
            aria-pressed={selected?.key === template.key}
            onClick={() => pick(template)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-[transform,background-color,border-color,color] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.97] active:duration-[160ms]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected?.key === template.key
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-background text-foreground hover:bg-accent',
            )}
          >
            {template.label}
          </button>
        ))}
      </div>
      {state.kind === 'confirming' && (
        <div role="group" aria-label="Replace your text" className="flex flex-wrap items-center gap-3 rounded-lg bg-muted px-3 py-2 text-sm">
          <span className="text-foreground">Replace your text?</span>
          <div className="ml-auto flex gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={keep}>
              Keep
            </Button>
            <Button type="button" size="sm" onClick={replace}>
              Replace
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
